import Big from 'big.js';
import { FormikProvider, useFormik } from 'formik';
import { useCallback } from 'react';
import * as yup from 'yup';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import SharesInput from '@/modules/common/components/SharesCountInput.tsx';
import UploadDocumentsBlock from '@/modules/common/components/UploadDocumentsBlock.tsx';
import { BigRoundMode } from '@/modules/common/constants/big-round-mode.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import { useCreateTransactionMutation } from '@/modules/investor-dashboard/queries/transactions.tsx';
import { useGetRegister } from '@/modules/issuer-dashboard/hooks/useGetRegister.tsx';
import { Button, ModalContentWrapper } from 'ui';
import { CreateTransactionRequest, TransactionType } from 'api';

const validationSchema = yup.object({
  total: yup.number().typeError('Total must be a number').moreThan(0, 'Total must be more than 0'),
  quantity: yup
    .number()
    .typeError('Quantity must be a number')
    .moreThan(0, 'Quantity must be more than 0'),
});

const initialValues: CreateTransactionRequest & { register_id?: number; total: number } = {
  register_class_id: 0,
  shareholder_id: 0,
  type: TransactionType.Subscription,
  quantity: 0,
  price_per_share: 0,
  memo: '',
  dividend_reinvestment: false,
  dividend_event_id: undefined,
  total: 0,
  document_data: [],
};

export interface SubscribeModalProps {
  mode?: TransactionType;
  register_id: number;
  register_class_id: number;
  price_per_share: number;
  shareholder_id: number;
  open: boolean;
  register_class_name: string;
  onClose: () => void;
}

export const SubscribeModal = ({
  open,
  register_id,
  register_class_id,
  price_per_share,
  shareholder_id,
  register_class_name,
  mode = TransactionType.Subscription,
  onClose,
}: SubscribeModalProps) => {
  const allowFractionalShares = useGetRegister(register_id)?.allows_fractional_shares ?? true;
  const formik = useFormik<typeof initialValues>({
    initialValues: {
      ...initialValues,
      type: mode,
      register_class_id,
      price_per_share,
      shareholder_id,
      register_id,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      await mutateAsync(values);
      resetForm();
      onClose();
    },
    enableReinitialize: true,
  });

  const { mutateAsync, isLoading } = useCreateTransactionMutation({ setErrors: formik.setErrors });

  const handleCloseModal = () => {
    formik.resetForm();
    onClose();
  };

  const handleChangeTotal = (total: number | string) => {
    formik.setFieldValue('total', total);
    formik.setFieldValue(
      'quantity',
      total
        ? Number(new Big(total).div(price_per_share).round(2, BigRoundMode.roundHalfUp).valueOf())
        : total,
    );
  };

  const handleChangeQuantity = (quantity: string | number) => {
    formik.setFieldValue('quantity', quantity);
    formik.setFieldValue(
      'total',
      quantity ? Number(new Big(quantity).mul(price_per_share).valueOf()) : quantity,
    );
  };

  const title = (
    <p className="text-[24px] leading-[32px] font-semibold">
      {mode === TransactionType.Subscription ? 'Subscribe to ' : 'Redeem '}
      <span className="text-blue-700">{register_class_name}</span>
    </p>
  );

  const handleChangeDocuments = useCallback(
    (filesData: string[]) => {
      formik.setFieldValue('document_data', filesData);
      formik.setFieldError('document_data', '');
    },
    [formik],
  );

  return (
    <ModalContentWrapper open={open} title={title} onClose={handleCloseModal}>
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col space-y-10" onSubmit={preventFormDefault}>
          <div className="w-full flex flex-col space-y-8">
            <CurrencyInput
              name="total"
              label={
                mode === TransactionType.Subscription
                  ? 'How much total you want to invest?'
                  : 'How much total you want to get?'
              }
              value={formik.values.total}
              error={formik.errors.total}
              onChange={handleChangeTotal}
            />
            <SharesInput
              name="quantity"
              label={
                mode === TransactionType.Subscription
                  ? 'amount of shares You will get'
                  : 'amount of shares You want to redeem'
              }
              value={formik.values.quantity}
              error={formik.errors.quantity}
              allowFractional={allowFractionalShares}
              onChange={handleChangeQuantity}
            />
          </div>
          <UploadDocumentsBlock
            label="Upload document JPG, PNG or PDF. Up to 10 mb (Optional)"
            error={formik.errors.document_data}
            onChange={handleChangeDocuments}
            value={formik.values.document_data || []}
          />
          <Button onClick={formik.submitForm} loading={isLoading} disabled={isLoading}>
            Submit
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
